import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary para capturar erros de renderização e evitar tela branca
 * Exibe uma página de erro amigável ao invés de quebrar completamente
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State | null {
    // DOM mutado por Chrome/extensões: não entrar na tela vermelha
    const message = String(error?.message || '').toLowerCase();
    const name = String((error as any)?.name || '').toLowerCase();
    if (
      name === 'notfounderror' ||
      name === 'hierarchyrequesterror' ||
      message.includes('insertbefore') ||
      message.includes('removechild') ||
      message.includes('failed to execute') ||
      message.includes('resizeobserver')
    ) {
      return null;
    }
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  isRecoverableClientError = (error: Error): boolean => {
    const message = String(error?.message || '').toLowerCase();
    const name = String((error as any)?.name || '').toLowerCase();
    const recoverablePatterns = [
      'resizeobserver loop limit exceeded',
      'resizeobserver loop completed with undelivered notifications',
      'script error',
      'non-error promise rejection captured',
      // Chrome + extensões/tradução/CDN alteram o DOM e o React quebra em insertBefore/removeChild
      'notfounderror',
      'removechild',
      'insertbefore',
      'failed to execute',
      'the node before which the new node is to be inserted',
      'hierarchyrequesterror',
      'the object can not be found here'
    ];
    if (name === 'notfounderror' || name === 'hierarchyrequesterror') {
      return true;
    }
    return recoverablePatterns.some((pattern) => message.includes(pattern));
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.isRecoverableClientError(error)) {
      // Erros transitórios de browser/mobile (DOM mutado por extensão/tradução)
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
      try {
        const key = 'cms_dom_recovery_ts';
        const last = Number(sessionStorage.getItem(key) || 0);
        const now = Date.now();
        // Evita loop: no máx. 1 reload automático a cada 15s
        if (!last || now - last > 15000) {
          sessionStorage.setItem(key, String(now));
          window.setTimeout(() => window.location.reload(), 50);
        }
      } catch (_) {}
      return;
    }

    // Log do erro para análise
    console.error('🚨 ErrorBoundary capturou um erro:', error);
    console.error('📋 Detalhes do erro:', errorInfo);
    
    // Enviar erro para serviço de monitoramento (opcional)
    this.logErrorToService(error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });
  }

  logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Aqui você pode enviar para Sentry, LogRocket, etc.
    // Por enquanto, apenas log no console
    try {
      const errorData = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      // Salvar no localStorage para análise posterior
      const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      errors.push(errorData);
      // Manter apenas os últimos 10 erros
      if (errors.length > 10) {
        errors.shift();
      }
      localStorage.setItem('app_errors', JSON.stringify(errors));
    } catch (e) {
      console.error('Erro ao salvar log de erro:', e);
    }
  };

  getCurrentSlug = (): string => {
    try {
      // Tentar extrair o slug da URL atual (ex: /meuslug/dashboard → "meuslug")
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        return pathParts[0];
      }
    } catch (_) {}
    // Fallback: tentar pegar do localStorage (auth-storage)
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        const panel = parsed?.state?.user?.panel || parsed?.user?.panel;
        if (panel) return panel;
      }
    } catch (_) {}
    return '';
  };

  handleReload = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    // Recarregar a página atual preservando o slug na URL
    window.location.reload();
  };

  handleGoHome = () => {
    const slug = this.getCurrentSlug();
    if (slug) {
      // Redirecionar para o login do slug correto
      window.location.href = `/${slug}/login`;
    } else {
      // Sem slug conhecido, ir para raiz (CmsSelector)
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      // Se tiver um fallback customizado, usar ele
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI de fallback padrão
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Ops! Algo deu errado
              </h1>
              <p className="text-gray-600 mb-6">
                Encontramos um problema ao carregar a página. Por favor, tente novamente.
              </p>
            </div>

            {this.state.error && (
              <div className="mb-6 p-4 bg-red-50 rounded-lg text-left">
                <p className="text-sm font-mono text-red-800 break-all">
                  <strong>Erro:</strong>{' '}
                  {this.state.error.message.length > 280
                    ? `${this.state.error.message.slice(0, 280)}…`
                    : this.state.error.message}
                </p>
                {process.env.NODE_ENV === 'development' && this.state.error.stack && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 cursor-pointer">
                      Ver stack trace
                    </summary>
                    <pre className="mt-2 text-xs text-red-700 overflow-auto max-h-40">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
                <p className="mt-3 text-xs text-gray-600">
                  Para suporte: o navegador também guarda os últimos erros na chave{' '}
                  <code className="bg-gray-100 px-1 rounded">app_errors</code> do armazenamento local deste site.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Recarregar Página
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Ir para Login
              </button>
              <button
                onClick={() => {
                  const slug = this.getCurrentSlug();
                  if (slug) {
                    window.location.href = `/${slug}/dashboard`;
                  } else {
                    window.location.href = '/';
                  }
                }}
                className="flex-1 px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors font-medium"
              >
                Ir para Dashboard
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Se o problema persistir, entre em contato com o suporte.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

